import { MenuItem } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { Menubar } from 'primeng/menubar';
import { TieredMenu } from 'primeng/tieredmenu';
import { NgOptimizedImage } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';

import { RoleCode } from '../../../models/user/user.enums';
import { ROLE_OPTIONS } from '../../../models/user/user.constants';
import { AuthService } from '../../../../core/services/auth.service';
import { TitleService } from '../../../../core/services/title.service';
import { KioskRouteService } from '../../../../core/services/kiosk-route.service';
import { UserDisplayService } from '../../../services/user-display.service';
import { SupportRequestDialogComponent } from '../../../components/support-request-dialog/support-request-dialog.component';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrl: './header.component.css',
	imports: [Avatar, Menubar, TieredMenu, NgOptimizedImage, RouterModule, SupportRequestDialogComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit {
	public menuItems: MenuItem[] = [];
	public userMenuItems: MenuItem[] = [];
	public allMenuItems: MenuItem[] = [];
	public title = signal('Default Title');
	public supportDialogVisible = signal(false);

	private readonly router = inject(Router);
	private readonly authService = inject(AuthService);
	private readonly titleService = inject(TitleService);
	private readonly kioskRouteService = inject(KioskRouteService);
	public readonly userDisplay = inject(UserDisplayService);

	constructor() {
		effect(() => {
			this.title.set(this.titleService.getTitle());
		});
	}

	ngOnInit(): void {
		this.buildMenuItems();
	}

	/**
	 * Constructs and initializes the menu items based on the user's role and permissions.
	 */
	private buildMenuItems(): void {
		const roles = this.authService.getRoles();
		const activeRole = this.authService.getActiveRole();
		const route = (path: string): string => this.kioskRouteService.resolveAppPath(path);

		this.menuItems = [
			{
				label: 'Requisitions',
				icon: 'pi pi-copy',
				visible: this.authService.isRole([RoleCode.SUPER_ADMIN]),
				items: [
					{
						label: 'Requisitions List',
						icon: 'pi pi-copy',
						routerLink: route('/requisitions')
					},
					{
						label: 'Jonas Exports List',
						icon: 'pi pi-file-export',
						routerLink: route('/jonas-export-files')
					}
				]
			},
			{
				label: 'Requisitions',
				icon: 'pi pi-copy',
				visible: this.authService.isRole([
					RoleCode.ADMIN,
					RoleCode.SUPERVISOR,
					RoleCode.LEAD_HAND,
					RoleCode.WAREHOUSE_PERSONNEL,
					RoleCode.CC_VIEWER,
					RoleCode.APPROVER
				]),
				routerLink: route('/requisitions')
			},
			{
				label: 'Base Jobs',
				icon: 'pi pi-briefcase',
				routerLink: route('/base-jobs'),
				visible: this.authService.isRole([RoleCode.ADMIN, RoleCode.SUPER_ADMIN])
			},
			{
				label: 'Warehouses',
				icon: 'pi pi-warehouse',
				routerLink: route('/warehouses'),
				visible: this.authService.isRole([RoleCode.ADMIN, RoleCode.SUPER_ADMIN])
			},
			{
				label: 'Items',
				icon: 'pi pi-shopping-cart',
				routerLink: route('/items'),
				visible: this.authService.isRole([RoleCode.ADMIN, RoleCode.SUPER_ADMIN, RoleCode.WAREHOUSE_PERSONNEL])
			},
			{
				label: 'Reports',
				icon: 'pi pi-th-large',
				visible: this.authService.isRole([RoleCode.ADMIN, RoleCode.SUPER_ADMIN]),
				items: [
					{
						label: 'Requisition Report',
						icon: 'pi pi-table',
						routerLink: route('/reports/requisitions-view')
					},
					{
						label: 'Cost Report',
						icon: 'pi pi-dollar',
						routerLink: route('/reports/requisitions-cost')
					},
					{
						label: 'Cost Reports Dashboard',
						icon: 'pi pi-chart-line',
						routerLink: route('/reports/requisitions-cost-dashboard')
					}
				]
			},
			{
				label: 'Users & Devices',
				icon: 'pi pi-users',
				visible: this.authService.isRole([RoleCode.SUPER_ADMIN]),
				items: [
					{
						label: 'Users',
						icon: 'pi pi-users',
						routerLink: route('/users')
					},
					{
						label: 'Devices',
						icon: 'pi pi-tablet',
						routerLink: route('/devices')
					}
				]
			},
			{
				label: 'Logs',
				icon: 'pi pi-history',
				visible: this.authService.isRole([RoleCode.SUPER_ADMIN]),
				items: [
					{
						label: 'Email Logs',
						icon: 'pi pi-envelope',
						routerLink: route('/logs/email-logs')
					},
					{
						label: 'User Actions Logs',
						icon: 'pi pi-history',
						routerLink: route('/logs/user-actions-logs')
					}
				]
			},
			{
				label: 'Settings',
				icon: 'pi pi-cog',
				visible: this.authService.isRole([RoleCode.SUPER_ADMIN]),
				items: [
					{
						label: 'Regions',
						icon: 'pi pi-map-marker',
						routerLink: route('/settings/regions')
					},
					{
						label: 'App Settings',
						icon: 'pi pi-sliders-h',
						routerLink: route('/settings/app-settings')
					},
					{
						label: 'Device Settings',
						icon: 'pi pi-tablet',
						routerLink: route('/settings/device-settings')
					}
				]
			}
		];

		this.userMenuItems = [
			{
				label: 'Change Role',
				visible: roles.length > 1,
				icon: 'pi pi-arrow-right-arrow-left',
				items: roles.map(role => ({
					label: ROLE_OPTIONS[role]?.label || role,
					icon: role === activeRole ? 'pi pi-circle-on font-bold' : 'pi pi-circle-off font-bold',
					iconStyle: { color: this.userDisplay.getRoleColor(role) },
					command: () => {
						this.performRoleSwitch(role);
					},
					disabled: role === activeRole
				}))
			},
			{
				label: 'Support Request',
				icon: 'pi pi-question-circle',
				command: () => this.openSupportDialog()
			},
			{
				separator: true
			},
			{
				label: 'Sign Out',
				icon: 'pi pi-sign-out',
				command: () => this.logout()
			}
		];

		this.allMenuItems = [...this.menuItems, ...this.userMenuItems];
	}

	/**
	 * Performs the actual role switch and page reload.
	 */
	private async performRoleSwitch(role: RoleCode): Promise<void> {
		const currentPath = this.router.url;

		try {
			await this.router.navigateByUrl('/?rc=1', { skipLocationChange: true });
			const canNavigate = await this.router.navigateByUrl(currentPath, {
				state: { roleSwitchPending: role }
			});

			if (canNavigate) {
				this.authService.setActiveRole(role);
				const currentUrl = window.location.href;
				const url = new URL(currentUrl);
				url.searchParams.set('rc', '1');
				sessionStorage.setItem('lastUrl', url.href);
				window.location.href = url.href;
			}
		} catch {
			console.error('Role change failed');
		}
	}

	/**
	 * Logs the user out of the application.
	 */
	public logout(): void {
		this.authService.signOut();
		this.router.navigateByUrl(this.kioskRouteService.getSignInPath(), { replaceUrl: true }).then();
	}

	/**
	 * Opens the support request dialog.
	 */
	public openSupportDialog(): void {
		this.supportDialogVisible.set(true);
	}

	/**
	 * Returns the home route for the current route namespace.
	 *
	 * @return {string} Home path for standard or kiosk mode.
	 */
	public getHomeLink(): string {
		return this.kioskRouteService.getDefaultHomePath();
	}
}
